import React, { useEffect, useState } from 'react';
import { apiUrl } from './utils/api.js';

const RegistrationWizard = React.lazy(() => import('./RegistrationWizard'));

export default function Profile({
    token,
    onLogin,
    onLogout,
    onClose,
    playerStats = null,
    dailyClaimed = false,
    dailyLoading = false,
    onClaimDaily = null,
}) {
    const [profile, setProfile] = useState({ display_name: '', avatar: '', class: '', bio: '' });
    const [rpg, setRpg] = useState(null);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showRegistrationWizard, setShowRegistrationWizard] = useState(false);
    const [saved, setSaved] = useState(false);
    const [loginError, setLoginError] = useState('');

    useEffect(() => {
        if (!token) {
            setRpg(null);
            return;
        }

        let cancelled = false;
        const defaultProfile = { display_name: '', avatar: '', class: '', bio: '' };

        const loadProfile = async () => {
            setLoading(true);
            try {
                const res = await fetch(apiUrl('/api/users/me'), {
                    headers: { Authorization: `Bearer ${token}` },
                });
                let payload = null;
                try {
                    payload = await res.json();
                } catch (err) {
                    payload = null;
                }

                if (!res.ok) {
                    if (res.status === 401 && typeof onLogout === 'function') {
                        onLogout();
                    }
                    const message = payload && payload.error
                        ? payload.error
                        : `Failed to load profile (${res.status})`;
                    throw new Error(message);
                }

                if (!payload || !payload.user) {
                    throw new Error('Profile payload missing user');
                }

                if (!cancelled) {
                    setProfile(payload.user.profile || defaultProfile);
                    setRpg(payload.user.rpg || null);
                }
            } catch (error) {
                console.error('Error fetching profile:', error);
                if (!cancelled) {
                    setProfile(defaultProfile);
                    setRpg(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadProfile();
        return () => {
            cancelled = true;
        };
    }, [token, onLogout]);

    const handleSave = () => {
        if (!token) return;
        setLoading(true);
        fetch(apiUrl('/api/users/me'), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(profile),
        })
            .then((r) => r.json())
            .then((data) => {
                setProfile(data.user.profile || {});
                setRpg(data.user.rpg || rpg);
                setEditing(false);
                setSaved(true);
                setTimeout(() => setSaved(false), 2000);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    const handleLogin = () => {
        if (!username || !password) {
            setLoginError('Enter your username and password.');
            return;
        }
        setLoginError('');
        setLoading(true);
        fetch(apiUrl('/api/users/login'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
        })
            .then(async (r) => {
                let data = null;
                try {
                    data = await r.json();
                } catch {
                    data = null;
                }
                if (!r.ok || !data || !data.token) {
                    const message = data && data.error
                        ? data.error
                        : 'Incorrect username or password.';
                    throw new Error(message);
                }
                if (data.user && data.user.rpg) setRpg(data.user.rpg);
                onLogin(data.token, data.user);
            })
            .catch((error) => {
                setLoginError(
                    error && error.message
                        ? error.message
                        : "Couldn't sign in. Please try again.",
                );
            })
            .finally(() => setLoading(false));
    };

    if (!token) {
        // Show registration wizard
        if (showRegistrationWizard) {
            return (
                <React.Suspense fallback={<div className='profile-box'>Loading registration…</div>}>
                    <RegistrationWizard
                        onSuccess={(token, user) => {
                            if (user && user.rpg) {
                                setRpg(user.rpg);
                            }
                            onLogin(token, user);
                            setShowRegistrationWizard(false);
                        }}
                        onCancel={() =>
                            setShowRegistrationWizard(false)}
                    />
                </React.Suspense>
            );
        }

        // Show simple login form
        return (
            <div className='profile-box'>
                <h3>Sign In</h3>
                <form
                    className='login-form'
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleLogin();
                    }}
                >
                    <input
                        placeholder='username'
                        autoComplete='username'
                        value={username}
                        onChange={(e) => {
                            setUsername(e.target.value);
                            if (loginError) setLoginError('');
                        }}
                    />
                    <input
                        placeholder='password'
                        type='password'
                        autoComplete='current-password'
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            if (loginError) setLoginError('');
                        }}
                    />
                    {loginError && (
                        <div className='login-error' role='alert'>
                            {loginError}
                        </div>
                    )}
                    <button type='submit' disabled={loading} className='btn-primary'>
                        {loading ? 'Signing In...' : 'Sign In'}
                    </button>
                </form>
                <div className='auth-divider'>
                    <span>Don't have an account?</span>
                </div>
                <button
                    onClick={() => setShowRegistrationWizard(true)}
                    className='btn-ghost btn-full-width'
                    disabled={loading}
                >
                    Create Account
                </button>
            </div>
        );
    }

    // Prefer the live app-level stats (kept fresh by XP payloads); fall back to
    // the copy fetched with the profile.
    const stats = playerStats || rpg;
    const xpPercent = stats
        ? Math.max(0, Math.min(100, Math.round((stats.xp_progress || 0) * 100)))
        : 0;

    return (
        <div className='profile-box'>
            <button onClick={onClose} className='close-button'>X</button>
            {saved && <div className='saved-message'>Saved!</div>}
            <h3>Profile</h3>
            {loading ? <div className='spinner'></div> : (
                <div>
                    {!editing
                        ? (
                            <div>
                                <div className='profile-header'>
                                    <div className='avatar-placeholder'>
                                        {profile.display_name
                                            ? profile.display_name.charAt(0).toUpperCase()
                                            : 'U'}
                                    </div>
                                    <div>
                                        <strong>Name:</strong> {profile.display_name || ''}
                                    </div>
                                </div>
                                <div>
                                    <strong>Class:</strong> {profile.class || ''}
                                </div>
                                <div>
                                    <strong>Bio:</strong> {profile.bio || ''}
                                </div>
                                {stats && (
                                    <div className='profile-rpg-stats'>
                                        <div className='profile-section-title'>
                                            Adventurer Progress
                                        </div>
                                        <div className='rpg-level-row'>
                                            <span className='rpg-level'>Level {stats.level}</span>
                                            <span className='rpg-total-xp'>
                                                Total XP {stats.xp}
                                            </span>
                                        </div>
                                        <div className='rpg-xp-track'>
                                            <div
                                                className='rpg-xp-fill'
                                                style={{ width: `${xpPercent}%` }}
                                            />
                                        </div>
                                        <div className='rpg-xp-caption'>
                                            {stats.xp_into_level} / {stats.xp_for_level}{' '}
                                            XP ({xpPercent}%)
                                        </div>
                                        {onClaimDaily && (
                                            <>
                                                <button
                                                    className='btn-primary btn-full-width'
                                                    onClick={onClaimDaily}
                                                    disabled={dailyClaimed || dailyLoading}
                                                >
                                                    {dailyClaimed
                                                        ? 'Daily Bonus Claimed'
                                                        : dailyLoading
                                                        ? 'Claiming...'
                                                        : 'Claim Daily Bonus'}
                                                </button>
                                                <div className='rpg-daily-hint'>
                                                    {dailyClaimed
                                                        ? 'Come back tomorrow for more XP.'
                                                        : 'Log your focus each day for bonus XP.'}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                <div style={{ marginTop: 8 }}>
                                    <button onClick={() => setEditing(true)} className='btn-ghost'>
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => {
                                            setRpg(null);
                                            onLogout();
                                        }}
                                        className='btn-ghost'
                                    >
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )
                        : (
                            <div className='profile-edit-form'>
                                <div className='form-group'>
                                    <label>Name</label>
                                    <input
                                        value={profile.display_name || ''}
                                        onChange={(e) =>
                                            setProfile((p) => ({
                                                ...p,
                                                display_name: e.target.value,
                                            }))}
                                    />
                                </div>
                                <div className='form-group'>
                                    <label>Class</label>
                                    <input
                                        value={profile.class || ''}
                                        onChange={(e) =>
                                            setProfile((p) => ({ ...p, class: e.target.value }))}
                                    />
                                </div>
                                <div className='form-group'>
                                    <label>Bio</label>
                                    <textarea
                                        value={profile.bio || ''}
                                        onChange={(e) =>
                                            setProfile((p) => ({ ...p, bio: e.target.value }))}
                                    />
                                </div>
                                <div className='form-actions'>
                                    <button onClick={() => setEditing(false)} className='btn-ghost'>
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={loading}
                                        className='btn-primary'
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        )}
                </div>
            )}
        </div>
    );
}
